/**
 * tunnelDataMapper.ts
 * 
 * BUCKET 5: Tunnel UI Data Integration
 * Maps existing hook data to Tunnel UI schema
 * 
 * CRITICAL: This is a MAPPER LAYER ONLY - no network calls
 * Uses: useActiveSlate, useUserEntry, useEvents (existing hooks)
 * 
 * BUGFIX T4: Added team code normalization via normTeam()
 * - Fixes LA vs LAR mismatch between events and players
 * - Rams players now visible when filtering by game
 */

// @ts-ignore - theme.js doesn't have type declarations
import { TEAM_COLORS, COLORS } from '../constants/theme';
import { toDisplayStatTarget } from './tunnelDisplayMapper';
// T4 FIX: Import shared team normalization
import { normTeam } from '../utils/teamUtils';

// =============================================================================
// TYPE DEFINITIONS
// =============================================================================

export interface TunnelPlayer {
  id: number;
  name: string;
  team: string;
  position: string;
  opponent: string;
  teamColor: string;
  gameId: string;
  seasonAvg?: number;
  props: TunnelProp[];
}
export interface TunnelProp {
  id: number;
  statType: string;
  line: number;
  seasonAvg?: number;
  edgeValue?: number;
  defRank: number;
  defLabel: string;
}


export interface TunnelGame {
  id: string;
  label: string;
}

export interface TunnelRosterSlot {
  player_prop_id: number | null;
  side: 'OVER' | 'UNDER' | null;
  units: number;
  playerName?: string;
  statType?: string;
  line?: number;
}

// =============================================================================
// SLATE → TUNNEL MAPPERS
// =============================================================================

/**
 * Maps a slate player to Tunnel UI format
 */
export function mapSlatePlayerToTunnel(
  slatePlayer: any,
  events: any[]
): TunnelPlayer {
  const gameId = findGameIdForPlayer(slatePlayer, events);
  const teamColor = (TEAM_COLORS as Record<string, string>)[slatePlayer.team] 
    || COLORS?.slate500 
    || '#64748B';
  
  return {
    id: slatePlayer.id,
    name: slatePlayer.name || slatePlayer.player_name,
    team: slatePlayer.team,
    position: slatePlayer.position || slatePlayer.pos || 'FLEX',
    opponent: slatePlayer.opp || slatePlayer.opponent || 'TBD',
    teamColor,
    gameId,
    seasonAvg: slatePlayer.props?.[0]?.playerSeasonAvg ?? slatePlayer.props?.[0]?.playerAvg,
    props: (slatePlayer.props || []).map(mapPropToTunnel),
  };
}

/**
 * Maps a prop to Tunnel UI format
 */
export function mapPropToTunnel(prop: any): TunnelProp {
  const rawStat = prop.stat || prop.statType || prop.stat_type || '';
  return {
    id: prop.id,
    statType: toDisplayStatTarget(rawStat),
    line: prop.line ?? prop.lineValue ?? prop.line_value ?? 0,
    seasonAvg: prop.playerSeasonAvg ?? prop.playerAvg ?? prop.player_avg,
    edgeValue: prop.edgeValue ?? prop.edge,
    defRank: prop.opponentDefenseRank ?? prop.defRank ?? prop.opponent_defense_rank ?? 16,
    defLabel: getDefenseLabel(rawStat) || '',
  };
}

/**
 * Derives games from events array
 * T4 FIX: Normalize team codes for consistent game IDs
 */
export function deriveGamesFromEvents(events: any[]): TunnelGame[] {
  if (!events || events.length === 0) return [];
  
  return events.map(event => {
    const away = event.away_team || event.awayTeam || '';
    const home = event.home_team || event.homeTeam || '';
    // T4 FIX: Normalize team codes for game ID
    const normAway = normTeam(away);
    const normHome = normTeam(home);
    return {
      id: `${normAway.toLowerCase()}_${normHome.toLowerCase()}`,
      label: `${normAway} @ ${normHome}`,
    };
  });
}

// =============================================================================
// ENTRY → ROSTER MAPPERS
// =============================================================================

/**
 * Maps an entry from useUserEntry to Tunnel roster format
 */
export function mapEntryToTunnelRoster(entry: any): TunnelRosterSlot[] {
  if (!entry?.roster || !Array.isArray(entry.roster)) {
    return createEmptyRoster();
  }
  
  // Entry roster is already slot-indexed
  const result = createEmptyRoster();
  
  for (const slot of entry.roster) {
    const index = (slot.slot || slot.slot_index || 1) - 1;
    if (index >= 0 && index < 7) {
      result[index] = {
        player_prop_id: slot.prop?.id ?? slot.player_prop_id ?? null,
        side: slot.side ?? null,
        units: slot.units ?? 0,
        playerName: slot.player?.name,
        statType: slot.prop?.stat,
        line: slot.prop?.line,
      };
    }
  }
  
  return result;
}

/**
 * Creates empty 7-slot roster
 */
export function createEmptyRoster(): TunnelRosterSlot[] {
  return Array(7).fill(null).map(() => ({
    player_prop_id: null,
    side: null,
    units: 0,
  }));
}

// =============================================================================
// ROSTER → SAVE PAYLOAD (Backend Schema Match)
// =============================================================================

const SLOT_LABELS = ['QB', 'RB1', 'RB2', 'WR1', 'WR2', 'TE', 'FLEX'];

/**
 * Builds save payload matching backend RosterSlotInput schema
 * 
 * CRITICAL: Backend expects `playerAvg` and `edge`, NOT `playerSeasonAvg`/`edgeValue`
 */
export function buildSavePayload(
  roster: TunnelRosterSlot[],
  slatePlayers: any[]
): { roster: any[] } {
  return {
    roster: roster.map((slot, index) => {
      const propData = findPropById(slot.player_prop_id, slatePlayers);
      const playerData = findPlayerByPropId(slot.player_prop_id, slatePlayers);
      
      return {
        slot: index + 1,
        label: SLOT_LABELS[index],
        player: playerData ? {
          id: playerData.id,
          name: playerData.name || playerData.player_name,
          team: playerData.team,
          pos: playerData.position || playerData.pos || 'FLEX',
        } : null,
        prop: propData ? {
          id: propData.id,
          stat: propData.stat || propData.statType || propData.stat_type,
          line: propData.line ?? propData.lineValue ?? propData.line_value,
          playerAvg: propData.playerSeasonAvg ?? propData.playerAvg ?? propData.player_avg ?? 0,
          edge: propData.edgeValue ?? propData.edge ?? 0,
        } : null,
        side: slot.side,
        units: slot.units,
      };
    }),
  };
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Finds the game ID for a player based on their team and opponent.
 * T4 FIX: Uses normTeam() for all team code comparisons to handle LA/LAR mismatch.
 */
function findGameIdForPlayer(player: any, events: any[]): string {
  if (!events || events.length === 0) return 'unknown';
  
  // T4 FIX: Normalize player's team and opponent codes
  const playerTeam = normTeam(player.team);
  const playerOpp = normTeam(player.opp || player.opponent);
  
  const event = events.find(e => {
    // T4 FIX: Normalize event team codes for comparison
    const home = normTeam(e.home_team || e.homeTeam);
    const away = normTeam(e.away_team || e.awayTeam);
    return (home === playerTeam && away === playerOpp) ||
           (away === playerTeam && home === playerOpp);
  });
  
  if (!event) return 'unknown';
  
  // T4 FIX: Build game ID with normalized codes for consistency
  const away = normTeam(event.away_team || event.awayTeam || '');
  const home = normTeam(event.home_team || event.homeTeam || '');
  return `${away.toLowerCase()}_${home.toLowerCase()}`;
}

export function findPropById(propId: number | null, players: any[]): any | null {
  if (!propId || !players) return null;
  
  for (const player of players) {
    const prop = player.props?.find((p: any) => p.id === propId);
    if (prop) return prop;
  }
  return null;
}

export function findPlayerByPropId(propId: number | null, players: any[]): any | null {
  if (!propId || !players) return null;
  
  return players.find(p => p.props?.some((prop: any) => prop.id === propId)) ?? null;
}

function getDefenseLabel(statType: string): string {
  if (!statType) return '';
  const lower = statType.toLowerCase();
  if (lower.includes('pass') || lower.includes('completion')) return 'Pass Def';
  if (lower.includes('rush') || lower.includes('carry')) return 'Rush Def';
  if (lower.includes('rec') || lower.includes('target')) return 'Rec Def';
  return '';
}
